import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

export default function Dashboard({ user, onNewJob, onSelectJob, onEditJob }) {
  const [jobs, setJobs] = useState([]);
  const [appCounts, setAppCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState([]);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: jobData, error: jobError } = await supabase
        .from('jobs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (jobError) throw jobError;
      setJobs(jobData || []);

      if (jobData && jobData.length > 0) {
        const { data: appData } = await supabase
          .from('applications')
          .select('job_id');
        if (appData) {
          const counts = {};
          appData.forEach(a => { counts[a.job_id] = (counts[a.job_id] || 0) + 1; });
          setAppCounts(counts);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId, jobTitle) => {
    if (!window.confirm(`Job "${jobTitle || 'Unbenannt'}" wirklich löschen?`)) return;
    try {
      const { error } = await supabase.from('jobs').delete().eq('id', jobId);
      if (error) throw error;
      setJobs(prev => prev.filter(j => j.id !== jobId));
      setSelectedJobIds(prev => prev.filter(id => id !== jobId));
    } catch (err) {
      alert('Fehler beim Löschen: ' + err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedJobIds.length === 0) return;
    if (!window.confirm(`${selectedJobIds.length} Job${selectedJobIds.length > 1 ? 's' : ''} wirklich löschen?`)) return;
    try {
      const { error } = await supabase.from('jobs').delete().in('id', selectedJobIds);
      if (error) throw error;
      setJobs(prev => prev.filter(j => !selectedJobIds.includes(j.id)));
      setSelectedJobIds([]);
    } catch (err) {
      alert('Fehler beim Löschen: ' + err.message);
    }
  };

  const toggleSelectJob = (jobId) => {
    setSelectedJobIds(prev => prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]);
  };

  const toggleSelectAll = () => {
    setSelectedJobIds(prev => prev.length === jobs.length ? [] : jobs.map(j => j.id));
  };

  const handleSignOut = () => supabase.auth.signOut();

  const statusColor = (status) => {
    switch (status) {
      case 'active': return { bg: '#dcfce7', text: '#166534' };
      case 'draft': return { bg: '#fef9c3', text: '#854d0e' };
      case 'closed': return { bg: '#fee2e2', text: '#991b1b' };
      default: return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fc' }}>
      {/* Topbar */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 32px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        <div style={{ fontSize: '1.4rem', fontWeight: '800' }}>
          recrui<span style={{ color: '#667eea' }}>fly</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>{user?.email}</span>
          <button
            onClick={handleSignOut}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: '#374151',
              fontWeight: '500'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '40px 24px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: '800', margin: 0, color: '#111827' }}>
              Meine Jobs
            </h1>
            <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '0.95rem' }}>
              Übersicht aller deiner Stellenanzeigen
            </p>
          </div>
          <button
            onClick={onNewJob}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(102,126,234,0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            + Neuen Job anlegen
          </button>
        </div>

        {/* States */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⏳</div>
            <p>Jobs werden geladen...</p>
          </div>
        )}

        {error && (
          <div style={{
            padding: '16px 20px',
            background: '#fee2e2',
            borderRadius: '10px',
            color: '#dc2626',
            marginBottom: '24px'
          }}>
            Fehler beim Laden: {error}
          </div>
        )}

        {!loading && !error && jobs.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '100px 24px',
            background: 'white',
            borderRadius: '16px',
            border: '2px dashed #e5e7eb'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📋</div>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#374151', margin: '0 0 8px' }}>
              Noch keine Jobs vorhanden
            </h2>
            <p style={{ color: '#9ca3af', marginBottom: '28px', fontSize: '0.95rem' }}>
              Erstelle deine erste Stellenanzeige mit dem Wizard.
            </p>
            <button
              onClick={onNewJob}
              style={{
                padding: '12px 28px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(102,126,234,0.4)'
              }}
            >
              + Ersten Job anlegen
            </button>
          </div>
        )}

        {/* Bulk Actions */}
        {!loading && !error && jobs.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', color: '#666' }}>
              <input type="checkbox" checked={selectedJobIds.length === jobs.length && jobs.length > 0}
                onChange={toggleSelectAll} style={{ accentColor: '#6366f1', width: '16px', height: '16px' }} />
              Alle auswählen
            </label>
            {selectedJobIds.length > 0 && (
              <button onClick={handleBulkDelete} style={{
                padding: '6px 14px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                borderRadius: '8px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer'
              }}>
                {selectedJobIds.length} Job{selectedJobIds.length > 1 ? 's' : ''} löschen
              </button>
            )}
          </div>
        )}

        {/* Job Grid */}
        {!loading && !error && jobs.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {jobs.map(job => {
              const colors = statusColor(job.status);
              const isSelected = selectedJobIds.includes(job.id);
              return (
                <div
                  key={job.id}
                  onClick={() => onSelectJob(job)}
                  style={{
                    background: 'white',
                    borderRadius: '14px',
                    padding: '24px',
                    boxShadow: isSelected ? '0 0 0 2px #6366f1' : '0 1px 4px rgba(0,0,0,0.07)',
                    border: isSelected ? '1px solid #6366f1' : '1px solid #e5e7eb',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'; }}
                >
                  {/* Select Checkbox */}
                  <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '12px', right: '12px' }}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelectJob(job.id)}
                      style={{ accentColor: '#6366f1', width: '18px', height: '18px', cursor: 'pointer' }} />
                  </div>

                  {/* Status badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '0.78rem',
                      fontWeight: '600',
                      background: colors.bg,
                      color: colors.text
                    }}>
                      {job.status || 'Entwurf'}
                    </span>
                    <span style={{ color: '#d1d5db', fontSize: '1.2rem' }}>💼</span>
                  </div>

                  {/* Position */}
                  <h3 style={{
                    fontSize: '1.05rem',
                    fontWeight: '700',
                    color: '#111827',
                    margin: '0 0 6px',
                    lineHeight: 1.3
                  }}>
                    {job.position || job.title || 'Unbenannte Stelle'}
                  </h3>

                  {/* Meta */}
                  <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '16px', lineHeight: 1.6 }}>
                    {job.company && <div>🏢 {job.company}</div>}
                    {job.location && <div>📍 {job.location}</div>}
                    {job.contract_type || job.contractType
                      ? <div>📄 {job.contract_type || job.contractType}</div>
                      : null}
                  </div>

                  {/* Footer */}
                  <div style={{
                    borderTop: '1px solid #f3f4f6',
                    paddingTop: '12px',
                    fontSize: '0.8rem',
                    color: '#9ca3af',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>
                      {job.created_at
                        ? `Erstellt am ${new Date(job.created_at).toLocaleDateString('de-DE')}`
                        : ''}
                    </span>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {appCounts[job.id] > 0 && (
                        <span style={{
                          background: '#667eea',
                          color: 'white',
                          borderRadius: '20px',
                          padding: '2px 9px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {appCounts[job.id]} Bewerbung{appCounts[job.id] !== 1 ? 'en' : ''}
                        </span>
                      )}
                      {job.status === 'draft' && (
                        <button
                          onClick={e => { e.stopPropagation(); onEditJob(job); }}
                          style={{
                            padding: '3px 10px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Weiterbearbeiten →
                        </button>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); handleDeleteJob(job.id, job.position); }}
                        style={{
                          padding: '3px 10px',
                          background: 'none',
                          color: '#9ca3af',
                          border: '1px solid #e5e7eb',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => { e.target.style.color = '#dc2626'; e.target.style.borderColor = '#dc2626'; }}
                        onMouseLeave={e => { e.target.style.color = '#9ca3af'; e.target.style.borderColor = '#e5e7eb'; }}
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
